import config from '../configs/config';
import syncUpPlatformRequest from '../repositories/syncupPlatformRequest';

/**
 * IoT Firmware Repository
 * ========================
 * Provides data access layer for IoT firmware management operations.
 *
 * This repository handles all API interactions related to:
 * - Individual firmware management (CRUD operations)
 * - Firmware group management (create, update, delete groups)
 * - Device-to-group associations (add/remove devices from groups)
 * - File upload operations for firmware assets
 *
 * All methods follow consistent patterns:
 * - Accept product context for multi-tenant operations
 * - Support optional response status flag for detailed API responses
 * - Use centralized configuration for endpoints and headers
 * - Leverage syncUpPlatformRequest for standardized HTTP handling
 *
 * @module iotFirmwareRepository
 * @requires config - Application configuration including API endpoints
 * @requires syncUpPlatformRequest - Centralized HTTP request handler
 */
const iotFirmwareRepository = {
  getAllIotFirmware: async ({ product, responseStatus = false }) => {
    const {
      resources,
      basePath,
      httpMethods,
      headers
    } = config.apiSettings.syncUpPlatform;

    return await syncUpPlatformRequest({
      basePath: basePath.admimV3,
      responseStatus: responseStatus,
      urlSuffix: `${resources.iotFirmware}?`,
      httpMethod: httpMethods.GET,
      headers: {
        [headers.SyncUpProduct.key]: product
      }
    });
  },
  createIotFirmware: async ({ body, product, responseStatus = false }) => {
    const {
      resources,
      basePath,
      httpMethods,
      headers
    } = config.apiSettings.syncUpPlatform;

    return await syncUpPlatformRequest({
      basePath: basePath.admimV3,
      responseStatus: responseStatus,
      urlSuffix: resources.iotFirmware,
      body: body,
      httpMethod: httpMethods.POST,
      headers: {
        [headers.SyncUpProduct.key]: product
      }
    });
  },
  getIotFirmwareById: async ({ firmwareId, product }) => {
    const {
      resources,
      basePath,
      httpMethods,
      headers
    } = config.apiSettings.syncUpPlatform;

    return await syncUpPlatformRequest({
      basePath: basePath.admimV3,
      urlSuffix: `${resources.iotFirmware}/${firmwareId}`,
      httpMethod: httpMethods.GET,
      headers: {
        [headers.SyncUpProduct.key]: product
      }
    });
  },
  uploadIotFirmwareAsset: async ({
    firmwareId,
    body,
    product,
    responseStatus = false
  }) => {
    const {
      resources,
      basePath,
      httpMethods,
      headers
    } = config.apiSettings.syncUpPlatform;

    return await syncUpPlatformRequest({
      basePath: basePath.admimV3,
      responseStatus: responseStatus,
      urlSuffix: `${resources.iotFirmware}/${firmwareId}/upload`,
      httpMethod: httpMethods.POST,
      headers: {
        [headers.SyncUpProduct.key]: product
      },
      body: body,
      formData: true
    });
  },
  updateIotFirmware: async ({
    firmwareId,
    body,
    product,
    responseStatus = false
  }) => {
    const {
      resources,
      basePath,
      httpMethods,
      headers
    } = config.apiSettings.syncUpPlatform;

    return await syncUpPlatformRequest({
      basePath: basePath.admimV3,
      responseStatus: responseStatus,
      urlSuffix: `${resources.iotFirmware}/${firmwareId}`,
      httpMethod: httpMethods.PUT,
      headers: {
        [headers.SyncUpProduct.key]: product
      },
      body: body
    });
  },
  deleteIotFirmware: async ({
    firmwareId,
    product,
    responseStatus = false
  }) => {
    const {
      resources,
      basePath,
      httpMethods,
      headers
    } = config.apiSettings.syncUpPlatform;

    return await syncUpPlatformRequest({
      basePath: basePath.admimV3,
      responseStatus: responseStatus,
      urlSuffix: `${resources.iotFirmware}/${firmwareId}`,
      httpMethod: httpMethods.DELETE,
      headers: {
        [headers.SyncUpProduct.key]: product
      }
    });
  },
  getAllIotFirmwareGroups: async ({ product, responseStatus = false }) => {
    const {
      resources,
      basePath,
      httpMethods,
      headers
    } = config.apiSettings.syncUpPlatform;

    return await syncUpPlatformRequest({
      basePath: basePath.admimV3,
      responseStatus: responseStatus,
      urlSuffix: resources.iotFirmwareGroups,
      httpMethod: httpMethods.GET,
      headers: {
        [headers.SyncUpProduct.key]: product
      }
    });
  },
  getIotFirmwareDevicesInGroup: async ({
    groupId,
    product,
    responseStatus = false
  }) => {
    const {
      resources,
      basePath,
      httpMethods,
      headers
    } = config.apiSettings.syncUpPlatform;

    return await syncUpPlatformRequest({
      basePath: basePath.admimV3,
      responseStatus: responseStatus,
      urlSuffix: resources.iotFirmwareDevicesInGroup.replace(
        ':groupId',
        groupId
      ),
      httpMethod: httpMethods.GET,
      headers: {
        [headers.SyncUpProduct.key]: product
      }
    });
  },
  createIotFirmwareGroup: async ({ body, product, responseStatus = false }) => {
    const {
      resources,
      basePath,
      httpMethods,
      headers
    } = config.apiSettings.syncUpPlatform;

    return await syncUpPlatformRequest({
      basePath: basePath.admimV3,
      responseStatus: responseStatus,
      urlSuffix: resources.iotFirmwareGroups,
      httpMethod: httpMethods.POST,
      headers: {
        [headers.SyncUpProduct.key]: product
      },
      body: body
    });
  },
  updateIotFirmwareGroup: async ({
    groupId,
    body,
    product,
    responseStatus = false
  }) => {
    const {
      resources,
      basePath,
      httpMethods,
      headers
    } = config.apiSettings.syncUpPlatform;

    return await syncUpPlatformRequest({
      basePath: basePath.admimV3,
      responseStatus: responseStatus,
      urlSuffix: `${resources.iotFirmwareGroups}/${groupId}`,
      httpMethod: httpMethods.PUT,
      headers: {
        [headers.SyncUpProduct.key]: product
      },
      body: body
    });
  },
  deleteIotFirmwareGroup: async ({
    groupId,
    product,
    responseStatus = false
  }) => {
    const {
      resources,
      basePath,
      httpMethods,
      headers
    } = config.apiSettings.syncUpPlatform;

    return await syncUpPlatformRequest({
      basePath: basePath.admimV3,
      responseStatus: responseStatus,
      urlSuffix: `${resources.iotFirmwareGroups}/${groupId}`,
      httpMethod: httpMethods.DELETE,
      headers: {
        [headers.SyncUpProduct.key]: product
      }
    });
  },
  addDevicesToFirmwareGroup: async ({
    groupId,
    body,
    product,
    responseStatus = false
  }) => {
    const {
      resources,
      basePath,
      httpMethods,
      headers
    } = config.apiSettings.syncUpPlatform;

    return await syncUpPlatformRequest({
      basePath: basePath.admimV3,
      responseStatus: responseStatus,
      urlSuffix: resources.iotFirmwareDevicesInGroup.replace(
        ':groupId',
        groupId
      ),
      httpMethod: httpMethods.POST,
      body: body,
      headers: {
        [headers.SyncUpProduct.key]: product
      }
    });
  },

  /**
   * Removes device from firmware group
   * ==================================
   * Unassigns a specific device from its firmware group, making it
   * ungrouped and exempt from group-based firmware deployments.
   *
   * Removal scenarios:
   * - Device decommissioning or retirement
   * - Moving device to different group (use addDevicesToFirmwareGroup with forceReassign)
   * - Isolating problematic devices for troubleshooting
   * - Temporary exclusion from group deployments
   *
   * Removal effects:
   * - Device no longer receives group-based firmware updates
   * - Device remains in system but becomes ungrouped
   * - Individual device management still possible
   * - Historical deployment data preserved
   *
   * @async
   * @param {Object} params - Removal parameters
   * @param {string} params.groupId - Source firmware group
   * @param {Object} params.body - Device removal payload
   * @param {string} params.body.hardwareId - Device identifier to remove (IMEI, serial, etc.)
   * @param {string} [params.body.removalReason] - Documentation for removal
   * @param {string} params.product - Product context for access control
   * @param {boolean} [params.responseStatus=false] - Include removal metadata
   * @returns {Promise<Object>} Removal confirmation
   *
   * @throws {Error} 404 - Device not found in specified group
   * @throws {Error} 403 - Insufficient permissions for device removal
   *
   * @example
   * const removalResult = await iotFirmwareRepository.deleteDeviceFromFirmwareGroup({
   *   groupId: 'group_abc123',
   *   body: {
   *     hardwareId: '860123456789012',
   *     removalReason: 'Device reported for maintenance, temporary isolation'
   *   },
   *   product: 'syncup-drive',
   *   responseStatus: true
   * });
   *
   * console.log(`Device removed from group: ${removalResult.success}`);
   */
  deleteDeviceFromFirmwareGroup: async ({
    groupId,
    hardwareId,
    product,
    responseStatus = false
  }) => {
    const {
      resources,
      basePath,
      httpMethods,
      headers
    } = config.apiSettings.syncUpPlatform;

    return await syncUpPlatformRequest({
      basePath: basePath.admimV3,
      responseStatus: responseStatus,
      urlSuffix: resources.iotFirmwareDevicesInGroupDelete
        .replace(':groupId', groupId)
        .replace(':deviceId', hardwareId),
      httpMethod: httpMethods.DELETE,
      headers: {
        [headers.SyncUpProduct.key]: product
      }
    });
  }
};

export default iotFirmwareRepository;
